import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  addCalendarNote,
  deleteCalendarNote,
  getGiftingHolidays,
  listCalendarNotes,
  STICKY_COLORS,
  toISODate,
} from '../../lib/campaignCalendar';
import { CalendarIcon, PlusCircleIcon } from '../icons';
import { useConfirm } from '../../components/ConfirmDialog';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_LABEL = new Intl.DateTimeFormat('en-JM', { month: 'long', year: 'numeric' });

// Holidays that are specifically Jamaican public holidays, flagged as such —
// the rest of the list is retail/gifting dates that aren't country-specific.
const JAMAICAN = new Set(['Emancipation Day', 'Independence Day', 'National Heroes Day']);

function buildMonthGrid(year, month) {
  const firstOfMonth = new Date(year, month, 1);
  const gridStart = new Date(year, month, 1 - firstOfMonth.getDay());
  return Array.from({ length: 42 }, (_, i) => {
    const date = new Date(gridStart);
    date.setDate(gridStart.getDate() + i);
    return date;
  });
}

export default function CampaignCalendar() {
  const confirm = useConfirm();
  const { user } = useAuth();
  const [monthAnchor, setMonthAnchor] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCell, setActiveCell] = useState(null);
  const [noteBody, setNoteBody] = useState('');
  const [noteColor, setNoteColor] = useState(STICKY_COLORS[0]);
  const [saving, setSaving] = useState(false);

  const grid = useMemo(
    () => buildMonthGrid(monthAnchor.getFullYear(), monthAnchor.getMonth()),
    [monthAnchor]
  );

  const holidaysByDate = useMemo(() => {
    const years = new Set(grid.map((d) => d.getFullYear()));
    const map = new Map();
    years.forEach((year) => {
      getGiftingHolidays(year).forEach((h) => map.set(h.date, h.label));
    });
    return map;
  }, [grid]);

  const loadNotes = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await listCalendarNotes(toISODate(grid[0]), toISODate(grid[grid.length - 1]));
      setNotes(rows);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [grid]);

  useEffect(() => {
    loadNotes();
  }, [loadNotes]);

  const notesByDate = useMemo(() => {
    const map = new Map();
    notes.forEach((n) => {
      const list = map.get(n.note_date) || [];
      list.push(n);
      map.set(n.note_date, list);
    });
    return map;
  }, [notes]);

  const openForm = (iso) => {
    setActiveCell(iso);
    setNoteBody('');
    setNoteColor(STICKY_COLORS[0]);
  };

  const handleAddNote = async (iso) => {
    if (!noteBody.trim()) return;
    setSaving(true);
    try {
      await addCalendarNote({
        note_date: iso,
        body: noteBody.trim(),
        color: noteColor,
        created_by: user.id,
      });
      setActiveCell(null);
      await loadNotes();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteNote = async (id) => {
    if (!(await confirm({
      title: 'Delete this note?',
    }))) return;

    await deleteCalendarNote(id);
    await loadNotes();
  };

  const today = toISODate(new Date());
  const inMonth = (d) => d.getMonth() === monthAnchor.getMonth();

  // Counts describe the visible month only, and only things that actually
  // exist — notes you've written and holidays we compute. Nothing here is
  // padded with placeholder totals.
  const monthNotes = notes.filter((n) => inMonth(new Date(`${n.note_date}T00:00:00`)));
  const monthHolidays = grid.filter((d) => inMonth(d) && holidaysByDate.has(toISODate(d)));

  return (
    <div className="calendar">
      <div className="calendar-bar">
        <div className="calendar-nav">
          <button
            className="calendar-nav-button"
            onClick={() => setMonthAnchor((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1))}
            aria-label="Previous month"
          >
            ‹
          </button>
          <h3>{MONTH_LABEL.format(monthAnchor)}</h3>
          <button
            className="calendar-nav-button"
            onClick={() => setMonthAnchor((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1))}
            aria-label="Next month"
          >
            ›
          </button>
        </div>

        <button
          className="calendar-today-button"
          onClick={() => {
            const now = new Date();
            setMonthAnchor(new Date(now.getFullYear(), now.getMonth(), 1));
          }}
        >
          Today
        </button>
      </div>

      <div className="calendar-summary">
        <span className="calendar-stat">
          <CalendarIcon />
          <strong>{monthNotes.length}</strong> {monthNotes.length === 1 ? 'note' : 'notes'}
        </span>
        <span className="calendar-stat-divider" />
        <span className="calendar-stat">
          <strong>{monthHolidays.length}</strong> gifting{' '}
          {monthHolidays.length === 1 ? 'date' : 'dates'}
        </span>
      </div>

      {error && <p className="marketing-error">Couldn't load calendar notes: {error}</p>}

      <div className="calendar-weekdays">
        {WEEKDAYS.map((w) => (
          <div key={w} className="calendar-weekday">
            {w}
          </div>
        ))}
      </div>

      <div className="calendar-grid">
        {grid.map((date) => {
          const iso = toISODate(date);
          const isThisMonth = inMonth(date);
          const holiday = holidaysByDate.get(iso);
          const dayNotes = notesByDate.get(iso) || [];
          const isToday = iso === today;

          return (
            <div
              key={iso}
              className={
                'calendar-cell' +
                (isThisMonth ? '' : ' calendar-cell-outside') +
                (isToday ? ' calendar-cell-today' : '')
              }
            >
              <div className="calendar-cell-head">
                <span className={'calendar-daynum' + (isToday ? ' calendar-daynum-today' : '')}>
                  {date.getDate()}
                </span>
                {isToday && <span className="calendar-today-tag">Today</span>}
                <button
                  className="calendar-add"
                  onClick={() => openForm(iso)}
                  aria-label={`Add a note on ${iso}`}
                >
                  +
                </button>
              </div>

              {holiday && (
                <span className="calendar-holiday" title={holiday}>
                  {holiday}
                  {JAMAICAN.has(holiday) ? ' 🇯🇲' : ''}
                </span>
              )}

              <div className="calendar-events">
                {dayNotes.map((n) => (
                  <div className={`calendar-chip calendar-chip-${n.color}`} key={n.id}>
                    <span className="calendar-chip-body">{n.body}</span>
                    <button
                      className="calendar-chip-delete"
                      onClick={() => handleDeleteNote(n.id)}
                      title="Remove note"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>

              {activeCell === iso && (
                <div className="calendar-note-form">
                  <textarea
                    autoFocus
                    rows={2}
                    value={noteBody}
                    onChange={(e) => setNoteBody(e.target.value)}
                    placeholder="Campaign, deadline…"
                  />
                  <div className="calendar-note-form-row">
                    {STICKY_COLORS.map((c) => (
                      <button
                        key={c}
                        className={
                          `calendar-swatch calendar-swatch-${c}` + (noteColor === c ? ' active' : '')
                        }
                        onClick={() => setNoteColor(c)}
                        title={c}
                      />
                    ))}
                    <div className="calendar-note-form-actions">
                      <button onClick={() => setActiveCell(null)}>Cancel</button>
                      <button onClick={() => handleAddNote(iso)} disabled={saving || !noteBody.trim()}>
                        Add
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {loading && <p className="marketing-loading">Loading…</p>}

      <footer className="calendar-tip">
        <span className="calendar-tip-icon">
          <PlusCircleIcon />
        </span>
        <p>Hover any date and hit + to note a campaign, launch or deadline.</p>
      </footer>
    </div>
  );
}
